import {
	ApiCanDeleteUserValidationResponse,
	ApiPositionShare,
	ApiUser,
	UpdateApiUserBody,
} from '../api-types/user.api';
import { CanDeleteUserValidation, PositionShare, UpdateUser, User } from '../app-types/user.app';
import {
	transformApiContactPointsToAppContactPoints,
	transformAppContactPointToApiCreateContactPoint,
} from './contact-point.transform';
import { transformApiEmploymentToAppEmployment } from './employment.transform';
import { transformApiIntegrationOfficeToAppIntegrationOffice } from './integration-office.transform';
import { transformApiRoleToAppRole } from './role.transform';
import { transformApiIntegrationServiceToAppIntegrationService } from './service.transform';
import { transformApiSpecializationToAppSpecialization } from './specialization.transform';
import { normalizeBoolean } from './helpers';

/**
 * Transforms an ApiUser object to an app-specific User object.
 * @param input ApiUser
 * @description Transforms an ApiUser object to an app-specific User object.
 * This function maps the properties of the ApiUser to the User interface.
 * It is used to convert data received from the API into a format that the application can work with.
 * @returns User
 */
export const transformApiUserToAppUser = (input: ApiUser) => {
	const firstName = input.firstName ?? '';
	const lastName = input.lastName ?? '';
	const displayName = input.displayName ?? `${firstName} ${lastName}`.trim();
	const output: User = {
		id: input.id,
		objectId: input.objectId,
		displayName,
		firstName,
		lastName,
		title: input.title,
		streetAddress: input.streetAddress,
		postalCode: input.postalCode,
		city: input.city,
		businessPhones: input.businessPhones,
		mobilePhone: input.mobilePhone,
		email: input.email,
		notesOnAccessability: input.notesOnAccessability,
		genderId: input.genderId,
		positionShare: input.positionShare ? transformApiPositionShareToAppPositionShare(input.positionShare) : undefined,
		service: input.service ? transformApiIntegrationServiceToAppIntegrationService(input.service) : undefined,
		roles: input.roles ? transformApiRoleToAppRole(input.roles) : undefined,
		integrationServiceOffice: input.integrationServiceOffice
			? transformApiIntegrationOfficeToAppIntegrationOffice(input.integrationServiceOffice)
			: undefined,
		employment: input.employment ? transformApiEmploymentToAppEmployment(input.employment) : undefined,
		specialization: input.specialisation
			? transformApiSpecializationToAppSpecialization(input.specialisation)
			: undefined,
		isActive: normalizeBoolean(input.accountActivated),
		contactPoints: transformApiContactPointsToAppContactPoints(input.contactPoints || []),
		isSeverelyDisabled: input.isSeverelyDisabled,
	};
	return output;
};

export const transformApiUsersToAppUsers = (input: ApiUser[]): User[] => {
	return input.map(transformApiUserToAppUser);
};

/**
 * Transforms the UpdateUser object to an backend-specific User object.
 * @param input UpdateUser
 * @description Transforms the UpdateUser object to an backend-specific User object.
 * This function maps the properties of the UpdateUser to the ApiUserBody.
 * It is used to convert data to be sent to the API into a format that the backend can work with.
 * @returns UpdateUserBody
 */
export const transformUpdateUserToApiUserBody = (input: UpdateUser) => {
	const output: UpdateApiUserBody = {
		firstName: input.firstName,
		name: input.name,
		title: input.title,
		notesOnAccessibility: input.notesOnAccessibility,
		remark: input.remark,
		businessEmail: input.businessEmail,
		genderId: input.genderId,
		address: input.address,
		employmentId: input.employmentId,
		roleId: input.roleId,
		start: input.start,
		end: input.end,
		positionShare: input.positionShare,
		specializationId: input.specializationId,
		isSeverelyDisabled: input.isSeverelyDisabled,
		integrationServiceOfficeId: input.integrationServiceOfficeId,
	};

	if (input.contactPoints)
		output.contactPoints = input.contactPoints.map(transformAppContactPointToApiCreateContactPoint);

	return output;
};

export const transformApiPositionShareToAppPositionShare = (input: ApiPositionShare) => {
	const output: PositionShare = {
		id: input.id,
		share: input.share,
		start: input.start,
		end: input.end,
	};
	return output;
};

export const transformApiPositionSharesToAppPositionShares = (input: ApiPositionShare[]) => {
	const output: PositionShare[] = input.map(transformApiPositionShareToAppPositionShare);
	return output;
};

export const transformAppPositionShareToApiPositionShare = (input: PositionShare) => {
	const output: ApiPositionShare = {
		id: input.id,
		share: input.share,
		start: input.start,
		end: input.end,
	};
	return output;
};

export const transformApiCanDeleteUserValidationToAppCanDeleteUserValidation = (
	input: ApiCanDeleteUserValidationResponse,
): CanDeleteUserValidation => {
	return {
		hasClients: input.hasClients,
		hasEndDate: input.hasEndDate,
		canDelete: input.canDelete,
	};
};
